import {
    ApiResponse,
    getAxiosClient,
    TimeFrameValue,
    toFormData,
} from '@givelify/utils';
import { AxiosProgressEvent, AxiosRequestConfig } from 'axios';
import {
    MESSAGE_FILTER_VALUE,
    TRANSACTION_FILTER_VALUE,
} from '../../../models';
import { ApiRequest } from '../../ApiRequest';
import { DoneesEndpoints } from './doneesEndpoints';
import { IDoneeService } from './IDoneesService';
import {
    toFaithLeaderPayload,
    UpdateBankInfoRequest,
    UpdateFaithLeaderRequest,
    UpdateImageRequest,
    UpdateMailingAddressRequest,
    UpdateOrganizationAddressRequest,
    UpdateOrganizationInfoRequest,
    UpdatePhysicalAddressRequest,
    UpdatePrimaryRepRequest,
    UpdateDonorRequest,
    UpdateSocialInfoRequest,
    UpdateTaxIdRequest,
    UpdateMissionStatementAndOrgLogoRequest,
    UpdateAppProfileRequest,
    UpdateCoveredFeesSettingsRequest,
    UpdateIntegrationSettingsRequest,
    UpdateIntegrationCredentialRequest,
    UpdateDonorRecognitionSettingsRequest,
    AddCCBIntegrationRequest,
    AddScheduledReportRequest,
    UpdateScheduledReportRequest,
    PhysicalAddressCountry,
} from './requests';
import { UpdateCoverRequest } from './requests/UpdateCoverRequest';
import { UpdateLogoRequest } from './requests/UpdateLogoRequest';
import {
    GetBankAccountsResponse,
    GetDepositsResponse,
    GetDoneeResponse,
    GetGivingLinkResponse,
    GetIntegrationExportedDonationsResponse,
    GetIntegrationSettingsDetails,
    GetIntegrationStatsResponse,
    GetQuickGiveResponse,
    GetRefundedDonationsResponse,
    UpdateBankInfoResponse,
    UpdateDoneeResponse,
    UpdateDonorResponse,
    GetDonationActivityResponse,
    GetRepresentativesResponse,
    AddCCBIntegrationResponse,
    AddScheduledReportResponse,
    UpdateScheduledReportResponse,
    GetDonationsLifetimeStatisticsResponse,
    GetDonorsParams,
    GetDonorsResponse,
    CalendlyResponse,
} from './responses';

export class DoneesService implements IDoneeService {
    public async getCurrentDonee(
        config?: AxiosRequestConfig<any> | undefined,
    ): Promise<ApiResponse<GetDoneeResponse>> {
        const url = DoneesEndpoints.donee();
        return ApiRequest.get<GetDoneeResponse>(url, config);
    }

    public async getDonors(
        doneeId: number,
        params?: GetDonorsParams,
    ): Promise<ApiResponse<GetDonorsResponse>> {
        const url = DoneesEndpoints.donors(doneeId);
        return ApiRequest.get<GetDonorsResponse>(url, { params });
    }

    public async getBankAccounts(
        doneeId: number,
    ): Promise<ApiResponse<GetBankAccountsResponse>> {
        const url = DoneesEndpoints.bankAccounts(doneeId);
        return ApiRequest.get<GetBankAccountsResponse>(url);
    }

    public async updateBankInfo(
        doneeId: number,
        payload: UpdateBankInfoRequest,
    ): Promise<ApiResponse<UpdateBankInfoResponse>> {
        const url = DoneesEndpoints.bankInfo(doneeId);
        const formData = toFormData(payload);
        return ApiRequest.post<UpdateBankInfoResponse>(url, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
    }

    public async updateDonor(
        doneeId: number,
        donorId: string,
        payload: Partial<UpdateDonorRequest>,
    ): Promise<ApiResponse<UpdateDonorResponse>> {
        const url = DoneesEndpoints.donor(doneeId, donorId);
        return ApiRequest.patch<UpdateDonorResponse>(url, payload);
    }

    public async updatePrimaryRepresentative(
        doneeId: number,
        payload: UpdatePrimaryRepRequest,
    ): Promise<ApiResponse<unknown>> {
        const url = DoneesEndpoints.beneficiary(doneeId);
        const formData = toFormData(payload);
        return ApiRequest.post<unknown>(url, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
    }

    public async getQuickGive(
        doneeId: number,
    ): Promise<ApiResponse<GetQuickGiveResponse>> {
        const url = DoneesEndpoints.quickGive(doneeId);
        return ApiRequest.get<GetQuickGiveResponse>(url);
    }

    public async updateQuickGive(
        doneeId: number,
        payload: any,
    ): Promise<ApiResponse<unknown>> {
        const url = DoneesEndpoints.quickGive(doneeId);
        return ApiRequest.put<unknown>(url, payload);
    }

    public async getRefundedDonations(
        doneeId: number,
        timeFrame: TimeFrameValue,
        envelopeIds: number[],
        pageNumber: number,
        messageFilter?: MESSAGE_FILTER_VALUE[],
    ): Promise<ApiResponse<GetRefundedDonationsResponse>> {
        const url = DoneesEndpoints.donationsRefunded(
            doneeId,
            timeFrame,
            envelopeIds,
            pageNumber,
            messageFilter,
        );
        return ApiRequest.get<GetRefundedDonationsResponse>(url);
    }

    public async getDeposits(
        doneeId: number,
        timeFrame: TimeFrameValue,
        transactionFilter: any,
        onlyDate?: boolean,
        pageNumber?: number,
    ): Promise<ApiResponse<GetDepositsResponse>> {
        const url = DoneesEndpoints.deposits(
            doneeId,
            timeFrame,
            transactionFilter,
            onlyDate,
            pageNumber,
        );
        return ApiRequest.get<GetDepositsResponse>(url);
    }

    public async getDonationActivity(
        doneeId: number,
        timeFrame: TimeFrameValue,
        transactionFilter: TRANSACTION_FILTER_VALUE,
        envelopeIds: number[],
        pageNumber?: number,
        messageFilter?: MESSAGE_FILTER_VALUE[],
        search?: string,
        sort?: string,
        order?: string,
    ): Promise<ApiResponse<GetDonationActivityResponse>> {
        const url = DoneesEndpoints.donations(
            doneeId,
            timeFrame,
            transactionFilter,
            envelopeIds,
            pageNumber,
            messageFilter,
            sort,
            search,
            order,
        );
        return ApiRequest.get<GetDonationActivityResponse>(url);
    }

    public async updateOrganizationLogo(
        doneeId: number,
        payload: UpdateImageRequest,
    ): Promise<ApiResponse<UpdateDoneeResponse>> {
        const url = DoneesEndpoints.donees(doneeId);
        return ApiRequest.patch<UpdateDoneeResponse>(url, {
            logo: payload.url || null,
            logoCroppedCoordinates: payload.croppedCoordinates,
        });
    }

    public async updateOrganizationBanner(
        doneeId: number,
        payload: UpdateImageRequest,
    ): Promise<ApiResponse<UpdateDoneeResponse>> {
        const url = DoneesEndpoints.donees(doneeId);
        return ApiRequest.patch<UpdateDoneeResponse>(url, {
            photo: payload.url || null,
            photoCroppedCoordinates: payload.croppedCoordinates,
        });
    }

    public async uploadOrganizationCover(
        doneeId: number,
        payload: UpdateCoverRequest,
        onProgress: (progress: AxiosProgressEvent) => void,
    ): Promise<ApiResponse<UpdateDoneeResponse>> {
        const url = DoneesEndpoints.uploadImage(doneeId);
        const formData = new FormData();
        formData.append('photo', payload.image);
        formData.append(
            'photoCroppedCoordinatesHeight',
            payload.photoCroppedCoordinatesHeight.toString(),
        );
        formData.append(
            'photoCroppedCoordinatesWidth',
            payload.photoCroppedCoordinatesWidth.toString(),
        );
        formData.append(
            'photoCroppedCoordinatesX',
            payload.photoCroppedCoordinatesX.toString(),
        );
        formData.append(
            'photoCroppedCoordinatesY',
            payload.photoCroppedCoordinatesY.toString(),
        );

        const client = getAxiosClient({
            onUploadProgress: (progress) => onProgress(progress),
            withCredentials: true,
        });

        return ApiRequest.post<UpdateDoneeResponse>(
            url,
            formData,
            undefined,
            client,
        );
    }

    public async uploadOrganizationLogo(
        doneeId: number,
        payload: UpdateLogoRequest,
        onProgress: (progress: AxiosProgressEvent) => void,
    ): Promise<ApiResponse<UpdateDoneeResponse>> {
        const url = DoneesEndpoints.uploadImage(doneeId);
        const formData = new FormData();
        formData.append('logo', payload.image);
        formData.append(
            'logoCroppedCoordinatesHeight',
            payload.logoCroppedCoordinatesHeight.toString(),
        );
        formData.append(
            'logoCroppedCoordinatesWidth',
            payload.logoCroppedCoordinatesWidth.toString(),
        );
        formData.append(
            'logoCroppedCoordinatesX',
            payload.logoCroppedCoordinatesX.toString(),
        );
        formData.append(
            'logoCroppedCoordinatesY',
            payload.logoCroppedCoordinatesY.toString(),
        );

        const client = getAxiosClient({
            onUploadProgress: (progress) => onProgress(progress),
            withCredentials: true,
        });

        return ApiRequest.post<UpdateDoneeResponse>(
            url,
            formData,
            undefined,
            client,
        );
    }

    public async updateOrganizationAddress(
        doneeId: number,
        payload: UpdateOrganizationAddressRequest,
    ): Promise<ApiResponse<UpdateDoneeResponse>> {
        const url = DoneesEndpoints.donees(doneeId);
        return ApiRequest.patch<UpdateDoneeResponse>(url, payload);
    }

    public async updateTaxId(
        doneeId: number,
        payload: UpdateTaxIdRequest,
    ): Promise<ApiResponse<UpdateDoneeResponse>> {
        const url = DoneesEndpoints.donees(doneeId);
        const formData = toFormData(payload);
        formData.append('_method', 'PATCH');
        return ApiRequest.post<UpdateDoneeResponse>(url, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
    }

    public async updateSocialInfo(
        doneeId: number,
        payload: UpdateSocialInfoRequest,
    ): Promise<ApiResponse<UpdateDoneeResponse>> {
        const url = DoneesEndpoints.donees(doneeId);
        return ApiRequest.patch<UpdateDoneeResponse>(url, payload);
    }

    public async updateMailingAddress(
        doneeId: number,
        payload: UpdateMailingAddressRequest,
    ): Promise<ApiResponse<UpdateDoneeResponse>> {
        const url = DoneesEndpoints.donees(doneeId);
        return ApiRequest.patch<UpdateDoneeResponse>(url, {
            mailingAddress: payload,
        });
    }

    public async updatePhysicalAddress(
        doneeId: number,
        payload: UpdatePhysicalAddressRequest & PhysicalAddressCountry,
        phone?: string,
    ): Promise<ApiResponse<UpdateDoneeResponse>> {
        const url = DoneesEndpoints.donees(doneeId);
        return ApiRequest.patch<UpdateDoneeResponse>(url, {
            physicalAddress: payload,
            phone: phone,
        });
    }

    public async updateAddress(
        doneeId: number,
        physicalAddress?: UpdatePhysicalAddressRequest & PhysicalAddressCountry,
        mailingAddress?: UpdateMailingAddressRequest & PhysicalAddressCountry,
        phone?: string,
    ): Promise<ApiResponse<UpdateDoneeResponse>> {
        const url = DoneesEndpoints.donees(doneeId);
        return ApiRequest.patch<UpdateDoneeResponse>(url, {
            physicalAddress,
            mailingAddress,
            phone: phone,
        });
    }

    public async updateReceiptState(
        doneeId: number,
        payload: boolean,
    ): Promise<ApiResponse<UpdateDoneeResponse>> {
        const url = DoneesEndpoints.donees(doneeId);
        return ApiRequest.patch<UpdateDoneeResponse>(url, {
            customReceipt: payload,
        });
    }

    public async updateMissionStatement(
        doneeId: number,
        payload: string,
    ): Promise<ApiResponse<UpdateDoneeResponse>> {
        const url = DoneesEndpoints.donees(doneeId);
        return ApiRequest.patch<UpdateDoneeResponse>(url, {
            missionStatement: payload,
        });
    }

    public async updateMissionStatementAndOrgLogo(
        doneeId: number,
        payload: UpdateMissionStatementAndOrgLogoRequest,
    ): Promise<ApiResponse<UpdateDoneeResponse>> {
        const url = DoneesEndpoints.donees(doneeId);
        return ApiRequest.patch<UpdateDoneeResponse>(url, payload);
    }

    public async updateOrganizationInfo(
        doneeId: number,
        payload: UpdateOrganizationInfoRequest,
    ): Promise<ApiResponse<UpdateDoneeResponse>> {
        const url = DoneesEndpoints.donees(doneeId);
        return ApiRequest.patch<UpdateDoneeResponse>(url, payload);
    }

    public async updateFaithLeader(
        doneeId: number,
        payload: UpdateFaithLeaderRequest,
        onProgress?: (progress: AxiosProgressEvent) => void,
    ): Promise<ApiResponse<UpdateDoneeResponse>> {
        const url = DoneesEndpoints.faithLeader(doneeId);
        if (payload.doNotDisplayFaithLeader) {
            return ApiRequest.delete<UpdateDoneeResponse>(url);
        }

        return ApiRequest.put<UpdateDoneeResponse>(
            url,
            toFaithLeaderPayload(payload.data),
            {
                onUploadProgress: (progress) => {
                    onProgress?.(progress);
                },
            },
        );
    }

    public async updateDoneeFaithLeader(
        doneeId: number,
        userId: number,
    ): Promise<ApiResponse<UpdateDoneeResponse>> {
        const url = DoneesEndpoints.donees(doneeId);
        return ApiRequest.patch<UpdateDoneeResponse>(url, {
            faithLeaderId: userId,
        });
    }

    public async updateAppProfile(
        doneeId: number,
        payload: UpdateAppProfileRequest,
    ): Promise<ApiResponse<UpdateDoneeResponse>> {
        const url = DoneesEndpoints.faithLeader(doneeId);
        return ApiRequest.post<UpdateDoneeResponse>(url, payload);
    }

    public async getGivingLink(
        doneeId: number,
    ): Promise<ApiResponse<GetGivingLinkResponse>> {
        const url = DoneesEndpoints.givingLink(doneeId);
        return ApiRequest.get<GetGivingLinkResponse>(url);
    }

    public async updateCoveredFeesSettings(
        doneeId: number,
        payload: UpdateCoveredFeesSettingsRequest,
    ): Promise<ApiResponse<UpdateDoneeResponse>> {
        const url = DoneesEndpoints.donees(doneeId);
        return ApiRequest.patch<UpdateDoneeResponse>(url, payload);
    }

    public async updateDonorRecognitionSettings(
        doneeId: number,
        payload: UpdateDonorRecognitionSettingsRequest,
    ): Promise<ApiResponse<UpdateDoneeResponse>> {
        const url = DoneesEndpoints.donees(doneeId);
        return ApiRequest.patch<UpdateDoneeResponse>(url, payload);
    }

    public async getIntegrationStats(
        doneeId: number,
    ): Promise<ApiResponse<GetIntegrationStatsResponse>> {
        const url = DoneesEndpoints.integrationStats(doneeId);
        return ApiRequest.get<GetIntegrationStatsResponse>(url);
    }

    public async getIntegrationDetails(
        doneeId: number,
    ): Promise<ApiResponse<GetIntegrationSettingsDetails>> {
        const url = DoneesEndpoints.integrationDetails(doneeId);
        return ApiRequest.get<GetIntegrationSettingsDetails>(url);
    }

    public async updateIntegrationSettings(
        doneeId: number,
        payload: UpdateIntegrationSettingsRequest,
    ): Promise<ApiResponse<GetIntegrationSettingsDetails>> {
        const url = DoneesEndpoints.integrationDetails(doneeId);
        return ApiRequest.put<GetIntegrationSettingsDetails>(url, {
            settings: payload,
        });
    }

    public async updateIntegrationCredential(
        doneeId: number,
        payload: UpdateIntegrationCredentialRequest,
    ): Promise<ApiResponse<GetIntegrationSettingsDetails>> {
        const url = DoneesEndpoints.integrationDetails(doneeId);
        return ApiRequest.put<GetIntegrationSettingsDetails>(url, payload);
    }

    public async addCCBIntegration(
        doneeId: number,
        payload: AddCCBIntegrationRequest,
    ): Promise<ApiResponse<AddCCBIntegrationResponse>> {
        const url = DoneesEndpoints.integrationRegistration(doneeId);
        return ApiRequest.post<AddCCBIntegrationResponse>(url, {
            type: 'CCB',
            credentials: payload,
        });
    }

    public async addScheduledReport(
        doneeId: number,
        payload: AddScheduledReportRequest,
    ): Promise<ApiResponse<AddScheduledReportResponse>> {
        const url = DoneesEndpoints.scheduledReports(doneeId);
        return ApiRequest.post<AddScheduledReportResponse>(url, payload, {
            headers: {
                'Enable-Feature-Toggles': 'scheduled-report-cadence',
            },
            withCredentials: true,
        });
    }

    public async updateScheduledReport(
        doneeId: number,
        scheduledReportId: number,
        payload: UpdateScheduledReportRequest,
    ): Promise<ApiResponse<UpdateScheduledReportResponse>> {
        const url = DoneesEndpoints.scheduledReport(doneeId, scheduledReportId);
        return ApiRequest.patch<UpdateScheduledReportResponse>(url, payload, {
            headers: {
                'Enable-Feature-Toggles': 'scheduled-report-cadence',
            },
            withCredentials: true,
        });
    }

    public async getIntegrationExportedDonations(
        doneeId: number,
        page: number,
        size: number,
    ): Promise<ApiResponse<GetIntegrationExportedDonationsResponse>> {
        const url = DoneesEndpoints.integrationExportedDonations(
            doneeId,
            page,
            size,
        );
        return ApiRequest.get<GetIntegrationExportedDonationsResponse>(url);
    }

    public async downloadIntegrationExportedDonations(
        doneeId: number,
        startDate: string,
        endDate: string,
    ): Promise<ApiResponse<unknown>> {
        const url = DoneesEndpoints.integrationExportedDonationsDownload(
            doneeId,
            startDate,
            endDate,
        );
        return ApiRequest.get<unknown>(url);
    }

    public async getRepresentatives(
        doneeId: number,
    ): Promise<ApiResponse<GetRepresentativesResponse>> {
        const url = DoneesEndpoints.representatives(doneeId);
        return ApiRequest.get<GetRepresentativesResponse>(url);
    }

    public async getDonationsLifetimeStatistics(
        doneeId: number,
    ): Promise<ApiResponse<GetDonationsLifetimeStatisticsResponse>> {
        const url = DoneesEndpoints.donationsLifetimeStatistics(doneeId);
        return ApiRequest.get<GetDonationsLifetimeStatisticsResponse>(url);
    }

    public async storeBookedMeetingDetails(
        eventId: string,
        inviteeId: string,
    ): Promise<ApiResponse<unknown>> {
        const url = DoneesEndpoints.storeBookedMeetingDetails();
        return ApiRequest.post<GetDonationsLifetimeStatisticsResponse>(url, {
            eventId,
            inviteeId,
        });
    }

    public async getBookedMeetingDetails(): Promise<
        ApiResponse<CalendlyResponse>
    > {
        const url = DoneesEndpoints.getBookedMeetingDetails();
        return ApiRequest.get<CalendlyResponse>(url);
    }
}
